import { BOX, INSPECTION_X } from './railLayout';
import {
  BOX_COUNT,
  INSPECTION_HOLD_SECONDS,
  LOOP_SECONDS,
  boxPassesAt,
  inspectionWindow,
} from './railTiming';

/**
 * ### The sound
 *
 * What the scene judges by is the sound the unit makes, so the sound has to
 * be seen leaving the unit and reaching the server. Two things carry that:
 * ripples rising off the top of the unit toward the capsule while it is
 * turned, and the dashed signal run from the microphone to the server
 * flowing while a unit is heard and standing still between units. A good
 * unit rings in smooth arcs; the defect rings in jagged ones, which is the
 * difference the microphone is there to catch.
 */

/** Radii of the three ripples, from the top of the unit outward. */
export const SOUND_ARC_RADII: ReadonlyArray<number> = [14, 24, 34];

/** Seconds between one ripple leaving the unit and the next. */
const RIPPLE_PERIOD_SECONDS = 0.4;

/** Seconds each ripple takes to fade, from the moment it appears. */
const RIPPLE_LIFE_SECONDS = 0.3;

/** Seconds between the ripples of one set, radius by radius. */
const RIPPLE_STAGGER_SECONDS = 0.08;

/** The arc's sweep, in degrees, either side of straight up. */
const ARC_HALF_SWEEP = 60;

/** Degrees between the points of a jagged arc, and how far they jut. */
const JAG_STEP = 10;
const JAG_DEPTH = 2.2;

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** The point on the ripple's circle, whose centre is the top of the unit. */
function arcPoint(radius: number, degrees: number): string {
  const x = radius * Math.cos(toRadians(-90 + degrees));
  const y =
    -BOX.height -
    BOX.halfDepth * 2 +
    radius * Math.sin(toRadians(-90 + degrees));

  return `${x.toFixed(1)},${y.toFixed(1)}`;
}

/**
 * ### soundArcPath
 *
 * One ripple above the unit, in the box's own coordinates: a smooth arc
 * for a good unit, or the same arc with its points pushed in and out by
 * turns for the defect, so the two sounds look as different as they are.
 *
 * @param radius Distance of the ripple from the top of the unit
 * @param jagged Whether to draw the defect's broken ring
 * @returns SVG path data
 */
export function soundArcPath(radius: number, jagged: boolean): string {
  if (!jagged) {
    return `M${arcPoint(radius, -ARC_HALF_SWEEP)} A${radius},${radius} 0 0 1 ${arcPoint(radius, ARC_HALF_SWEEP)}`;
  }

  const points: Array<string> = [];
  for (
    let degrees = -ARC_HALF_SWEEP, i = 0;
    degrees <= ARC_HALF_SWEEP;
    degrees += JAG_STEP, i += 1
  ) {
    points.push(
      arcPoint(radius + (i % 2 === 0 ? JAG_DEPTH : -JAG_DEPTH), degrees),
    );
  }

  return `M${points.join(' L')}`;
}

const pct = (seconds: number): string =>
  `${((seconds / LOOP_SECONDS) * 100).toFixed(4)}%`;

/**
 * The ripples of one radius, in the box's own time: sets leave the unit at
 * a steady rate for as long as it is turning, each ripple of a set a beat
 * after the one inside it, and each fading as it goes.
 */
function rippleKeyframes(index: number): string {
  const { up, down } = inspectionWindow();
  const stops: Array<string> = ['0%, 100% { opacity: 0; }'];

  for (
    let appears = up + index * RIPPLE_STAGGER_SECONDS;
    appears + RIPPLE_LIFE_SECONDS <= down;
    appears += RIPPLE_PERIOD_SECONDS
  ) {
    stops.push(
      `${pct(appears - 0.01)} { opacity: 0; }`,
      `${pct(appears)} { opacity: 1; }`,
      `${pct(appears + RIPPLE_LIFE_SECONDS)} { opacity: 0; }`,
    );
  }

  return `@keyframes prl-sound-${index} { ${stops.join(' ')} }`;
}

/** A stretch of the loop during which a unit is being heard. */
interface Hearing {
  from: number;
  to: number;
}

/**
 * The holds of every box as stretches of the shared loop, split where one
 * runs over the loop's end so every stretch lies inside it, in order.
 */
function hearings(): Array<Hearing> {
  const stretches: Array<Hearing> = [];
  for (let index = 0; index < BOX_COUNT; index += 1) {
    const from = boxPassesAt(index, INSPECTION_X);
    const to = from + INSPECTION_HOLD_SECONDS;
    if (to <= LOOP_SECONDS) {
      stretches.push({ from, to });
    } else {
      stretches.push(
        { from, to: LOOP_SECONDS },
        { from: 0, to: to - LOOP_SECONDS },
      );
    }
  }

  return stretches.sort((left, right) => left.from - right.from);
}

/** The signal run's dash pattern, whose period the flow has to respect. */
export const SIGNAL_DASH = { on: 6, off: 7 } as const;

/**
 * ### signalFlowKeyframes
 *
 * The dashed run from microphone to server, flowing toward the server
 * while a unit is heard and standing still between units. The dashes move
 * a whole number of periods per hold, so a loop's worth of flow ends
 * where it began and the loop restarts without a jump. The run also
 * brightens while it carries something.
 *
 * @returns The keyframes for the signal run
 */
export function signalFlowKeyframes(): string {
  const period = SIGNAL_DASH.on + SIGNAL_DASH.off;
  const dashesPerHold = 8;
  const rate = (dashesPerHold * period) / INSPECTION_HOLD_SECONDS;
  const idle = 'opacity: 0.55;';
  const stretches = hearings();
  const stops: Array<string> = [];
  let offset = 0;

  if (stretches[0] !== undefined && stretches[0].from > 0) {
    stops.push(`0% { stroke-dashoffset: 0; ${idle} }`);
  }
  for (const { from, to } of stretches) {
    stops.push(
      `${pct(from)} { stroke-dashoffset: ${offset.toFixed(2)}; ${idle} }`,
    );
    // A stretch cut short by the loop's end may be too brief to brighten
    if (to - from > 0.3) {
      stops.push(
        `${pct(from + 0.1)} { opacity: 1; }`,
        `${pct(to - 0.1)} { opacity: 1; }`,
      );
    }
    offset -= rate * (to - from);
    stops.push(
      `${pct(to)} { stroke-dashoffset: ${offset.toFixed(2)}; ${idle} }`,
    );
  }
  const last = stretches[stretches.length - 1];
  if (last !== undefined && last.to < LOOP_SECONDS) {
    stops.push(`100% { stroke-dashoffset: ${offset.toFixed(2)}; ${idle} }`);
  }

  return `@keyframes prl-signal-flow { ${stops.join(' ')} }`;
}

/**
 * ### soundKeyframes
 *
 * The ripples' keyframes, one per radius, and the signal run's flow.
 *
 * @returns CSS text to ship inside the drawing's stylesheet
 */
export function soundKeyframes(): string {
  return [
    ...SOUND_ARC_RADII.map((_, index) => rippleKeyframes(index)),
    signalFlowKeyframes(),
  ].join('\n');
}
